import { useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useToast } from "@/shared/providers/ToastProvider";
import { restoreAdminBackup } from "../api/adminOpsApi";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { isSuperAdminUser } from "../auth";

export function RestoreBackupPage() {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState(t.safecheck.system.backup.noFileSelected);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  if (!isSuperAdminUser()) {
    return <Navigate to="/dashboard/admin" replace />;
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setFileName(file?.name ?? t.safecheck.system.backup.noFileSelected);
  };

  const handleImport = async () => {
    if (!selectedFile) {
      showToast({ message: t.safecheck.system.backup.importInvalidFile, variant: "warning" });
      return;
    }

    if (!selectedFile.name.toLowerCase().endsWith(".sql")) {
      showToast({ message: t.safecheck.system.backup.importInvalidFile, variant: "warning" });
      return;
    }

    setLoading(true);
    try {
      const result = await restoreAdminBackup(selectedFile);
      showToast({ message: result.message, variant: "success" });
      setSelectedFile(null);
      setFileName(t.safecheck.system.backup.noFileSelected);
      if (inputRef.current) inputRef.current.value = "";
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : t.safecheck.system.backup.importInvalidFile,
        variant: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrumbs
        homeHref="/dashboard/admin"
        items={[
          { label: t.safecheck.nav.settings, href: "/dashboard/settings/user" },
          { label: t.safecheck.nav.restoreBackup },
        ]}
      />

      <div className="theme-card p-6">
        <p className="text-sm text-muted">{t.safecheck.system.backup.importInstruction}</p>

        <div className="mt-5 max-w-lg">
          <label
            htmlFor="restore-backup-file"
            className="flex cursor-pointer overflow-hidden rounded-lg border border-border bg-card text-sm"
          >
            <span className="shrink-0 border-r border-border bg-card-muted px-4 py-2.5 font-medium text-foreground">
              {t.safecheck.system.backup.chooseFile}
            </span>
            <span className="flex min-w-0 flex-1 items-center truncate px-4 py-2.5 text-muted">
              {fileName}
            </span>
          </label>
          <input
            id="restore-backup-file"
            ref={inputRef}
            type="file"
            accept=".sql"
            className="sr-only"
            onChange={handleFileChange}
          />
        </div>

        <button
          type="button"
          onClick={handleImport}
          disabled={loading}
          className="mt-5 rounded-lg bg-purple px-5 py-2.5 text-sm font-medium text-white shadow-[var(--shadow-sm)] transition-colors hover:bg-purple/90 disabled:opacity-60"
        >
          {loading ? "…" : t.safecheck.system.backup.importButton}
        </button>
      </div>
    </>
  );
}
